/**
 * Projects, and the four-step form that makes one.
 *
 * The form is deliberately a wizard rather than one long page: each step asks
 * one thing and then shows what the app found, so the answer can be checked
 * before the next question. Step two is the one that matters — picking a
 * folder runs the data scan there and then, and the findings become the
 * reasons subjects are left out. Everywhere else those are two activities with
 * a spreadsheet and somebody's memory in between.
 */

import { create } from "zustand";

import {
  listProjects,
  loadProject,
  saveProjectRecord,
  scanCohort,
  splitError,
} from "@/ipc/api";
import { fullSelection } from "@/pipeline/stages";
import type {
  CohortScan,
  CohortSubject,
  Exclusion,
  PipelineKind,
  Project,
  ProjectSummary,
  StageSelection,
} from "@/ipc/contract";

export type Step = "question" | "data" | "protocol" | "name";

export const STEPS: { id: Step; label: string; asks: string }[] = [
  { id: "question", label: "Question", asks: "What are you trying to find out?" },
  { id: "data", label: "Data", asks: "Which subjects?" },
  { id: "protocol", label: "Protocol", asks: "What will every subject receive?" },
  { id: "name", label: "Name", asks: "What is this project called, and where does output go?" },
];

/** A default a reader can accept without thinking, derived from the folder. */
function defaultName(root: string): string {
  const leaf = root.split(/[\\/]/).filter(Boolean).pop() ?? "";
  return leaf ? leaf.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim() : "";
}

interface DraftState {
  open: boolean;
  step: Step;
  busy: boolean;
  error: string | null;

  question: string;
  root: string;
  scan: CohortScan | null;
  /** Subject id → why it is out. Absent means it is in. */
  excluded: Record<string, { reason: string; fromScan: boolean }>;
  pipelineKind: PipelineKind;
  stages: StageSelection;
  name: string;
  outputRoot: string;

  begin: () => void;
  close: () => void;
  go: (step: Step) => void;
  set: (patch: Partial<Omit<DraftState, keyof DraftActions>>) => void;
  chooseRoot: (root: string) => Promise<void>;
  exclude: (subjectId: string, reason: string, fromScan: boolean) => void;
  include: (subjectId: string) => void;
  create: () => Promise<Project | null>;
}

type DraftActions =
  | "begin" | "close" | "go" | "set" | "chooseRoot" | "exclude" | "include" | "create";

const EMPTY = {
  step: "question" as Step,
  busy: false,
  error: null as string | null,
  question: "",
  root: "",
  scan: null as CohortScan | null,
  excluded: {} as Record<string, { reason: string; fromScan: boolean }>,
  pipelineKind: "dwi" as PipelineKind,
  // The whole pipeline, because that is what anyone means by "run the DWI
  // analysis"; unticking is the deliberate act, not ticking six boxes to get
  // the obvious thing.
  stages: fullSelection("dwi") as StageSelection,
  name: "",
  outputRoot: "",
};

export const useProjectDraft = create<DraftState>((set, get) => ({
  open: false,
  ...EMPTY,

  begin: () => set({ open: true, ...EMPTY }),
  close: () => set({ open: false }),
  go: (step) => set({ step, error: null }),
  set: (patch) => set(patch as Partial<DraftState>),

  chooseRoot: async (root) => {
    set({ busy: true, error: null, root });
    try {
      const scan = await scanCohort(root);
      // Anything the scan already calls an error is pre-excluded, with the
      // scan's own words as the reason — the reader can put it back, but the
      // default is not to run something that is going to fail.
      const excluded: Record<string, { reason: string; fromScan: boolean }> = {};
      for (const s of scan.subjects) {
        if (s.severity === "error") {
          const why = s.issues.find((i) => i.severity === "error")?.message ?? "Will fail as it is";
          excluded[s.subjectId] = { reason: why, fromScan: true };
        }
      }
      set({
        scan,
        excluded,
        busy: false,
        name: get().name || defaultName(root),
        outputRoot: get().outputRoot || `${root.replace(/[\\/]+$/, "")}-out`,
      });
    } catch (e) {
      const { message, detail } = splitError(e);
      set({ busy: false, scan: null, error: detail ? `${message}\n${detail}` : message });
    }
  },

  exclude: (subjectId, reason, fromScan) =>
    set((s) => ({ excluded: { ...s.excluded, [subjectId]: { reason, fromScan } } })),

  include: (subjectId) =>
    set((s) => {
      const next = { ...s.excluded };
      delete next[subjectId];
      return { excluded: next };
    }),

  create: async () => {
    const s = get();
    if (!s.scan) return null;
    const iso = new Date().toISOString();
    const included = s.scan.subjects
      .map((x) => x.subjectId)
      .filter((id) => !(id in s.excluded));
    const excluded: Exclusion[] = Object.entries(s.excluded).map(([subjectId, e]) => ({
      subjectId,
      reason: e.reason,
      fromScan: e.fromScan,
      iso,
    }));

    const project: Project = {
      id: `p${Date.now().toString(36)}`,
      name: s.name.trim() || defaultName(s.root) || "Untitled project",
      createdIso: iso,
      modifiedIso: iso,
      question: s.question.trim(),
      cohort: {
        root: s.root,
        scannedIso: s.scan.scannedIso,
        integrityCheckedIso: null,
        included,
        excluded,
      },
      protocol: { pipelineKind: s.pipelineKind, stages: s.stages, fixedIso: iso },
      outputRoot: s.outputRoot.trim(),
      runs: [],
      log: [
        {
          iso,
          kind: "cohort",
          text: `Screened ${s.scan.subjectCount} subjects in ${s.root}; ${included.length} included, ${excluded.length} excluded.`,
        },
        {
          iso,
          kind: "protocol",
          text: `Protocol fixed: ${s.pipelineKind.toUpperCase()}, ${s.stages.dwi.length + s.stages.pwi.length} stages.`,
        },
      ],
    };

    set({ busy: true, error: null });
    try {
      const saved = await saveProjectRecord(project);
      set({ open: false, busy: false });
      // Show the thing that was just made, rather than the list it is now on.
      useProjects.getState().setOpen(saved);
      void useProjects.getState().refresh();
      return saved;
    } catch (e) {
      set({ busy: false, error: splitError(e).message });
      return null;
    }
  },
}));

/** Subjects the scan found, with whether they are in and why not. */
export function partition(scan: CohortScan | null, excluded: Record<string, unknown>) {
  const all = scan?.subjects ?? [];
  return {
    included: all.filter((s: CohortSubject) => !(s.subjectId in excluded)),
    excluded: all.filter((s: CohortSubject) => s.subjectId in excluded),
  };
}

// ------------------------------------------------------------- the list ---

interface ProjectsState {
  items: ProjectSummary[];
  loading: boolean;
  open: Project | null;
  refresh: () => Promise<void>;
  openProject: (id: string) => Promise<void>;
  closeProject: () => void;
  setOpen: (project: Project | null) => void;
}

export const useProjects = create<ProjectsState>((set) => ({
  items: [],
  loading: false,
  open: null,
  refresh: async () => {
    set({ loading: true });
    try {
      set({ items: await listProjects(), loading: false });
    } catch {
      set({ items: [], loading: false });
    }
  },
  openProject: async (id) => {
    try {
      set({ open: await loadProject(id) });
    } catch {
      set({ open: null });
    }
  },
  closeProject: () => set({ open: null }),
  setOpen: (project) => set({ open: project }),
}));
